/**
 * Faction standing (plan §6 M2-05, spec §9.1): a pure command that moves one standing and the price multipliers
 * a standing gives at a station of that faction. Standings live in −100..100 (`ops.clampStanding`).
 */
import { SIM_FACTION_IDS, type SimFactionId } from '../data/m2Schemas'
import { clampStanding, withStanding } from './ops'
import { fail, invalidAmount, ok, type CareerEffect, type CmdResult, type CmdSuccess } from './result'
import type { Career } from './types'

/**
 * Adds `delta` standing with `faction` (clamped to −100..100). The `standingChanged` effect carries the change
 * actually applied; a change the clamp swallows returns the same career and no effect.
 */
export function adjustStanding(career: Career, faction: SimFactionId, delta: number): CmdResult<Career> {
  if (!Number.isFinite(delta)) return invalidAmount('standing change', delta)
  if (!SIM_FACTION_IDS.includes(faction)) {
    return fail('invalidAmount', `Unknown faction: ${String(faction)}.`, { faction: String(faction) })
  }
  const before = career.standings[faction]
  const after = clampStanding(before + delta)
  if (after === before) return ok(career)
  const effects: CareerEffect[] = [{ kind: 'standingChanged', faction, delta: after - before, value: after }]
  return ok(withStanding(career, faction, after - before), effects)
}

/** Sell-side price multiplier for a standing: `1 + swing × s / 100` (a friend pays more). */
export function standingPriceMult(standing: number, swing: number): number {
  return 1 + (swing * clampStanding(standing)) / 100
}

/** Buy-side price multiplier for a standing: `1 − swing × s / 100` (a friend charges less). */
export function standingBuyMult(standing: number, swing: number): number {
  return 1 - (swing * clampStanding(standing)) / 100
}

/** One standing change from the strategic worker (M5 S-M2-13), already mapped to an M2 faction. */
export interface CareerStandingDelta {
  faction: SimFactionId
  delta: number
  /** A loss never takes the standing below this; a standing already below it is left alone (D-335 D4). */
  floor?: number
}

/**
 * The part of `delta` that a floor lets through from `before`: a gain, or no floor, passes whole; a loss stops at
 * `floor` (0 when the standing is already at or below it).
 */
export function flooredDelta(before: number, delta: number, floor: number | undefined): number {
  if (floor === undefined || !(delta < 0)) return delta
  if (before <= floor) return 0
  return before + delta < floor ? floor - before : delta
}

/**
 * Applies a batch of standing changes (M5 S-M2-13: the worker's `standingDeltas`) in order, each clamped to
 * −100..100 like `adjustStanding`. Non-finite deltas and unknown factions are skipped, not refused, so one bad row
 * never drops the rest of the batch. A row with a `floor` loses at most down to it (`flooredDelta`). One
 * `standingChanged` effect per change the clamp lets through.
 */
export function applyStandingDeltas(career: Career, deltas: readonly CareerStandingDelta[]): CmdSuccess<Career> {
  let next = career
  const effects: CareerEffect[] = []
  for (const d of deltas) {
    const before = SIM_FACTION_IDS.includes(d.faction) ? next.standings[d.faction] : 0
    const r = adjustStanding(next, d.faction, flooredDelta(before, d.delta, d.floor))
    if (!r.ok) continue
    next = r.value
    effects.push(...r.effects)
  }
  return ok(next, effects)
}
